import type { StaticImageData } from 'next/image'

import autumnCreek from '@/assets/photographs/autumn-creek.jpg'
import beeEatersLanding from '@/assets/photographs/bee-eaters-landing.jpg'
import beeEatersWire from '@/assets/photographs/bee-eaters-wire.jpg'
import blackHeadedWagtail from '@/assets/photographs/black-headed-wagtail.jpg'
import blackStorkFishing from '@/assets/photographs/black-stork-fishing.png'
import blackStorkOverhead from '@/assets/photographs/black-stork-overhead.jpg'
import blackWingedStilt from '@/assets/photographs/black-winged-stilt.jpg'
import brownBear from '@/assets/photographs/brown-bear.jpg'
import buzzardChick from '@/assets/photographs/buzzard-chick.jpg'
import buzzardInRain from '@/assets/photographs/buzzard-in-rain.jpg'
import dalmatianPelican from '@/assets/photographs/dalmatian-pelican.jpg'
import flamingoColony from '@/assets/photographs/flamingo-colony.jpg'
import flamingoFormation from '@/assets/photographs/flamingo-formation.jpg'
import foxBehindTrunk from '@/assets/photographs/fox-behind-trunk.jpg'
import foxInTheGrass from '@/assets/photographs/fox-in-the-grass.jpg'
import goldfinch from '@/assets/photographs/goldfinch.jpg'
import greenLizard from '@/assets/photographs/green-lizard.jpg'
import greyPartridge from '@/assets/photographs/grey-partridge.jpg'
import gullOverDivjaka from '@/assets/photographs/gull-over-divjaka.jpg'
import hoodedCrow from '@/assets/photographs/hooded-crow.jpg'
import hoopoeInTheGrass from '@/assets/photographs/hoopoe-in-the-grass.jpg'
import hoopoeOnPipe from '@/assets/photographs/hoopoe-on-pipe.jpg'
import jay from '@/assets/photographs/jay.jpg'
import kingfisher from '@/assets/photographs/kingfisher.jpg'
import lapwingCalling from '@/assets/photographs/lapwing-calling.jpg'
import lapwingChick from '@/assets/photographs/lapwing-chick.jpg'
import littleEgretBacklit from '@/assets/photographs/little-egret-backlit.jpg'
import littleEgretGlasswort from '@/assets/photographs/little-egret-glasswort.jpg'
import longEaredOwl from '@/assets/photographs/long-eared-owl.jpg'
import noseHornedViper from '@/assets/photographs/nose-horned-viper.jpg'
import nurseryWebSpider from '@/assets/photographs/nursery-web-spider.jpg'
import ospreyGreyLight from '@/assets/photographs/osprey-grey-light.jpg'
import ospreyWithCatch from '@/assets/photographs/osprey-with-catch.jpg'
import peregrineFalcon from '@/assets/photographs/peregrine-falcon.jpg'
import radoniqiLake from '@/assets/photographs/radoniqi-lake.jpg'
import redBackedShrike from '@/assets/photographs/red-backed-shrike.jpg'
import redDeerSnow from '@/assets/photographs/red-deer-snow.jpg'
import redSquirrel from '@/assets/photographs/red-squirrel.jpg'
import reedIsland from '@/assets/photographs/reed-island.jpg'
import riverBridge from '@/assets/photographs/river-bridge.jpg'
import spiderWebs from '@/assets/photographs/spider-webs.jpg'
import storkOnDome from '@/assets/photographs/stork-on-dome.jpg'
import storksOnNest from '@/assets/photographs/storks-on-nest.jpg'
import tawnyOwlChicks from '@/assets/photographs/tawny-owl-chicks.jpg'
import tawnyOwlet from '@/assets/photographs/tawny-owlet.jpg'
import turtleDoves from '@/assets/photographs/turtle-doves.jpg'
import twoIslands from '@/assets/photographs/two-islands.jpg'

export type Realm = 'sky' | 'water' | 'forest' | 'field'

export interface PhotoSource {
    label: string
    href: string
}

export interface Photograph {
    slug: string
    plate: number
    title: string
    latin?: string
    realm: Realm
    location?: string
    note: string
    alt: string
    image: StaticImageData
    tone: string
    focus?: string
    source: PhotoSource
}

export interface RealmInfo {
    id: Realm
    numeral: string
    name: string
    subtitle: string
    blurb: string
    tone: string
}

export const realms: Array<RealmInfo> = [
    {
        id: 'sky',
        numeral: 'I',
        name: 'Sky',
        subtitle: 'Raptors, storks and pelicans',
        blurb: 'Birds held at the top of a wingbeat. The hardest frames to keep, and the reason he saved for his first long lens: to follow the migrating birds no phone could catch.',
        tone: '#0f1c30'
    },
    {
        id: 'water',
        numeral: 'II',
        name: 'Water',
        subtitle: 'Lagoons, lakes and rivers',
        blurb: 'Lagoons on the Albanian coast, reservoirs and mountain rivers in Kosovo. Water is where the wild gathers, and where it is easiest to lose.',
        tone: '#0a2729'
    },
    {
        id: 'forest',
        numeral: 'III',
        name: 'Forest',
        subtitle: 'Under cover',
        blurb: 'Owls in birch and oak, foxes at the tree line, deer in deep snow. Fifteen camera traps keep watch across Kosovo when he cannot.',
        tone: '#10170f'
    },
    {
        id: 'field',
        numeral: 'IV',
        name: 'Field',
        subtitle: 'Close to the ground',
        blurb: 'Hedgerows, wires and field edges: the ordinary places where the hoopoe, his favourite subject, comes to feed.',
        tone: '#e8e1d2'
    }
]

const fromPx = (id: number): PhotoSource => ({
    label: '500px',
    href: `https://500px.com/photo/${id}/`
})

const fromInterview: PhotoSource = {
    label: 'Active Albania interview, 2021',
    href: 'https://www.activealbania.com/whats-like-to-be-the-wildlife-photographer-arian-mavriqi/'
}

const fromKatror: PhotoSource = {
    label: 'Katror, via his Facebook page',
    href: 'https://katror.info/en/kosova-tjeter-ne-syte-e-mjeshtrit-te-fotografise/'
}

type PhotographEntry = Omit<Photograph, 'plate'>

const entries: Array<PhotographEntry> = [
    {
        slug: 'osprey-with-catch',
        title: 'Osprey',
        latin: 'Pandion haliaetus',
        realm: 'sky',
        note: 'Full wingspan, coming straight at the lens, the catch held tight beneath.',
        alt: 'An osprey flying toward the camera with a fish in its talons, wings fully spread against a clear blue sky.',
        image: ospreyWithCatch,
        tone: '#5d89bf',
        focus: '50% 45%',
        source: fromPx(307380215)
    },
    {
        slug: 'black-stork-overhead',
        title: 'Black stork',
        latin: 'Ciconia nigra',
        realm: 'sky',
        location: 'Gjilan, Kosovo',
        note: 'Seen from below, legs trailing, the green sheen of the neck catching the light.',
        alt: 'A black stork flying overhead with wings spread, its white belly and red legs visible against a blue sky.',
        image: blackStorkOverhead,
        tone: '#6a9ad6',
        focus: '45% 50%',
        source: fromInterview
    },
    {
        slug: 'dalmatian-pelican',
        title: 'Dalmatian pelican',
        latin: 'Pelecanus crispus',
        realm: 'sky',
        note: 'Known in Albanian as the curly pelican, for the loose feathers on its nape.',
        alt: 'A Dalmatian pelican gliding overhead, its long wings and pale body lit against a deep blue sky.',
        image: dalmatianPelican,
        tone: '#3e60a0',
        focus: '45% 50%',
        source: fromInterview
    },
    {
        slug: 'little-egret-backlit',
        title: 'Little egret',
        latin: 'Egretta garzetta',
        realm: 'sky',
        note: 'Sun behind the wings turns every flight feather into paper.',
        alt: 'A little egret in flight, sunlight glowing through its white wings against a deep blue sky.',
        image: littleEgretBacklit,
        tone: '#1f6db3',
        focus: '40% 50%',
        source: fromPx(1006243426)
    },
    {
        slug: 'gull-over-divjaka',
        title: 'Gull',
        latin: 'Larus sp.',
        realm: 'sky',
        location: 'Divjakë, Albania',
        note: 'Rim-lit against a navy sky above the lagoon coast.',
        alt: 'A gull soaring directly overhead with wings spread, rim-lit in white against a dark navy sky.',
        image: gullOverDivjaka,
        tone: '#0f2449',
        focus: '50% 55%',
        source: fromInterview
    },
    {
        slug: 'flamingo-formation',
        title: 'Greater flamingos',
        latin: 'Phoenicopterus roseus',
        realm: 'sky',
        note: 'Five birds, necks outstretched, in a loose line across a pastel sky.',
        alt: 'Five greater flamingos flying in loose formation, pink and black wings against a pale blue sky.',
        image: flamingoFormation,
        tone: '#9fb2ca',
        focus: '60% 45%',
        source: fromPx(1006243549)
    },
    {
        slug: 'black-winged-stilt',
        title: 'Black-winged stilt',
        latin: 'Himantopus himantopus',
        realm: 'sky',
        note: 'Pink legs trailing far behind the tail, black wings slicing the blue.',
        alt: 'A black-winged stilt in flight with long pink legs trailing behind, black wings against a blue sky.',
        image: blackWingedStilt,
        tone: '#3b78b4',
        focus: '45% 50%',
        source: fromPx(307379559)
    },
    {
        slug: 'lapwing-calling',
        title: 'Northern lapwing',
        latin: 'Vanellus vanellus',
        realm: 'sky',
        note: 'Calling in mid-flight, the thin crest swept back.',
        alt: 'A northern lapwing calling in flight with its bill open, wings raised against a bright blue sky.',
        image: lapwingCalling,
        tone: '#3e98da',
        focus: '50% 50%',
        source: fromPx(307379943)
    },
    {
        slug: 'osprey-grey-light',
        title: 'Osprey',
        latin: 'Pandion haliaetus',
        realm: 'sky',
        note: 'Low light, a heavy catch, the slow beat home.',
        alt: 'An osprey flying toward the camera carrying a fish, against a muted grey background.',
        image: ospreyGreyLight,
        tone: '#6a6366',
        focus: '48% 50%',
        source: fromPx(1006264784)
    },
    {
        slug: 'peregrine-falcon',
        title: 'Peregrine falcon',
        latin: 'Falco peregrinus',
        realm: 'sky',
        note: 'The fastest bird on Earth, at rest on concrete and rust.',
        alt: 'A peregrine falcon perched on a rusted concrete ledge, looking to the side.',
        image: peregrineFalcon,
        tone: '#9a8d7d',
        focus: '45% 40%',
        source: fromPx(1007373608)
    },
    {
        slug: 'flamingo-colony',
        title: 'Flamingo colony',
        latin: 'Phoenicopterus roseus',
        realm: 'water',
        note: 'From above, a whole flock becomes a single shape drifting across the lagoon.',
        alt: 'An aerial view of a large flock of flamingos wading across blue lagoon water.',
        image: flamingoColony,
        tone: '#2a5b77',
        focus: '55% 50%',
        source: fromPx(1006264678)
    },
    {
        slug: 'black-stork-fishing',
        title: 'Black stork',
        latin: 'Ciconia nigra',
        realm: 'water',
        note: 'A small fish caught mid-toss, the shallows turned to gold.',
        alt: 'A black stork standing in golden water with a small fish held in its open red bill.',
        image: blackStorkFishing,
        tone: '#e6b03c',
        focus: '58% 50%',
        source: fromKatror
    },
    {
        slug: 'little-egret-glasswort',
        title: 'Little egret',
        latin: 'Egretta garzetta',
        realm: 'water',
        note: 'Yellow feet, black bill, and a border of glasswort turning red at the water’s edge.',
        alt: 'A little egret standing on a stony shore in front of red and green glasswort, with water behind.',
        image: littleEgretGlasswort,
        tone: '#6b5b3c',
        focus: '55% 40%',
        source: fromPx(1006243536)
    },
    {
        slug: 'reed-island',
        title: 'Reed island',
        realm: 'water',
        note: 'A hut on a raft of reeds, alone in dark water, seen from the air.',
        alt: 'An aerial view of a small hut with a blue roof on a reed island surrounded by dark water.',
        image: reedIsland,
        tone: '#13233a',
        focus: '55% 50%',
        source: fromPx(307380585)
    },
    {
        slug: 'radoniq-lake',
        title: 'Radoniq Lake',
        realm: 'water',
        location: 'Gjakova, Kosovo',
        note: 'Light breaking through over the mountains, the reservoir gone deep blue.',
        alt: 'An aerial view of Radoniq Lake under heavy clouds, with rays of light falling on the mountains behind.',
        image: radoniqiLake,
        tone: '#111a2a',
        focus: '50% 55%',
        source: fromInterview
    },
    {
        slug: 'two-islands',
        title: 'Two islands',
        realm: 'water',
        note: 'Reed islands drifting apart on olive water, one of them carrying a hut.',
        alt: 'An aerial view of two small reed islands on green water, one with a hut on it.',
        image: twoIslands,
        tone: '#3c471e',
        focus: '50% 50%',
        source: fromPx(307380067)
    },
    {
        slug: 'stone-bridge',
        title: 'Stone bridge',
        realm: 'water',
        note: 'A long exposure smooths the river to milk beneath an old stone arch.',
        alt: 'A long exposure of a turquoise river flowing over rocks beneath an old stone arch bridge in a dark gorge.',
        image: riverBridge,
        tone: '#1c2a2b',
        focus: '45% 50%',
        source: fromPx(307380843)
    },
    {
        slug: 'kingfisher',
        title: 'Common kingfisher',
        latin: 'Alcedo atthis',
        realm: 'water',
        note: 'Turquoise and rust on a bare twig, waiting for the water to move.',
        alt: 'A common kingfisher perched on a thin branch, its turquoise back and orange breast against a warm brown background.',
        image: kingfisher,
        tone: '#6c5340',
        focus: '40% 50%',
        source: fromPx(1007373441)
    },
    {
        slug: 'fox-behind-the-trunk',
        title: 'Red fox',
        latin: 'Vulpes vulpes',
        realm: 'forest',
        note: 'Half hidden behind a trunk, curiosity winning over caution.',
        alt: 'A young red fox peering out from behind a tree trunk, ears up, looking at the camera.',
        image: foxBehindTrunk,
        tone: '#47563f',
        focus: '40% 45%',
        source: fromPx(1006243484)
    },
    {
        slug: 'fox-in-the-grass',
        title: 'Red fox',
        latin: 'Vulpes vulpes',
        realm: 'forest',
        location: 'Kamenica, Kosovo',
        note: 'Eye contact through the summer grass of Kamenica.',
        alt: 'A red fox standing in long grass and looking straight at the camera.',
        image: foxInTheGrass,
        tone: '#363626',
        focus: '35% 40%',
        source: fromInterview
    },
    {
        slug: 'red-squirrel',
        title: 'Red squirrel',
        latin: 'Sciurus vulgaris',
        realm: 'forest',
        location: 'Kamenica, Kosovo',
        note: 'Ear tufts lit from behind, clinging to a lichen-covered trunk.',
        alt: 'A red squirrel with tufted ears clinging to a lichen-covered tree trunk, lit by low sun.',
        image: redSquirrel,
        tone: '#5a4535',
        focus: '45% 35%',
        source: fromInterview
    },
    {
        slug: 'long-eared-owl',
        title: 'Long-eared owl',
        latin: 'Asio otus',
        realm: 'forest',
        note: 'Perfectly still in a birch, the autumn leaves doing the hiding.',
        alt: 'A long-eared owl perched on a birch branch among yellow and green autumn leaves.',
        image: longEaredOwl,
        tone: '#a7b2b7',
        focus: '55% 35%',
        source: fromPx(1007373503)
    },
    {
        slug: 'tawny-owl-chicks',
        title: 'Tawny owl chicks',
        latin: 'Strix aluco',
        realm: 'forest',
        note: 'Four owlets packed into a hollow trunk, all eyes on the intruder.',
        alt: 'Four fluffy tawny owl chicks huddled together inside a hollow tree trunk, looking up.',
        image: tawnyOwlChicks,
        tone: '#8b5a39',
        focus: '50% 45%',
        source: fromInterview
    },
    {
        slug: 'tawny-owlet',
        title: 'Tawny owl fledgling',
        latin: 'Strix aluco',
        realm: 'forest',
        note: 'Out of the nest, not yet a hunter.',
        alt: 'A young tawny owl with downy feathers perched on a mossy branch among green leaves.',
        image: tawnyOwlet,
        tone: '#b6c79e',
        focus: '45% 40%',
        source: fromPx(307379533)
    },
    {
        slug: 'buzzard-in-the-rain',
        title: 'Common buzzard',
        latin: 'Buteo buteo',
        realm: 'forest',
        note: 'Sitting out the rain in a bare tree above the meadow.',
        alt: 'A common buzzard perched in a leafless tree during rain, with a green hillside behind.',
        image: buzzardInRain,
        tone: '#4b6a39',
        focus: '55% 40%',
        source: fromPx(307379739)
    },
    {
        slug: 'buzzard-chick',
        title: 'Buzzard chick',
        latin: 'Buteo buteo',
        realm: 'forest',
        note: 'Still in down, with the hooked bill already in place.',
        alt: 'A close-up of a fluffy grey raptor chick with a yellow cere and dark eye, against a green background.',
        image: buzzardChick,
        tone: '#79a04a',
        focus: '45% 45%',
        source: fromPx(307379637)
    },
    {
        slug: 'red-deer-in-snow',
        title: 'Red deer',
        latin: 'Cervus elaphus',
        realm: 'forest',
        note: 'A young stag in deep snow, the whole scene reduced to sepia.',
        alt: 'A red deer stag with antlers standing in deep snow among bare trees, in sepia tones.',
        image: redDeerSnow,
        tone: '#d8d1c4',
        focus: '45% 50%',
        source: fromPx(307381051)
    },
    {
        slug: 'brown-bear',
        title: 'Brown bear',
        latin: 'Ursus arctos',
        realm: 'forest',
        note: 'Tongue out, coat ruffled, entirely unbothered by the lens.',
        alt: 'A close-up of a brown bear’s face with its tongue hanging out.',
        image: brownBear,
        tone: '#4e3e2d',
        focus: '45% 45%',
        source: fromPx(1006243442)
    },
    {
        slug: 'eurasian-jay',
        title: 'Eurasian jay',
        latin: 'Garrulus glandarius',
        realm: 'forest',
        note: 'Crest raised, the blue wing patch catching the light against a cloudless sky.',
        alt: 'A Eurasian jay perched on a bare branch against a clear blue sky, crest raised and blue wing feathers visible.',
        image: jay,
        tone: '#3d86d5',
        focus: '55% 35%',
        source: fromPx(307380123)
    },
    {
        slug: 'webs-in-the-mist',
        title: 'Webs in the mist',
        realm: 'forest',
        note: 'Spider webs strung between thorns, beaded with moisture.',
        alt: 'Several spider webs stretched across thin thorny branches, with a misty blue-grey background.',
        image: spiderWebs,
        tone: '#55636c',
        focus: '50% 40%',
        source: fromPx(128186247)
    },
    {
        slug: 'autumn-creek',
        title: 'Autumn creek',
        realm: 'forest',
        note: 'Fallen leaves gathered among the stones of a mountain stream.',
        alt: 'A long exposure of a small stream flowing between stones covered with orange autumn leaves.',
        image: autumnCreek,
        tone: '#39404c',
        focus: '50% 50%',
        source: fromPx(129103919)
    },
    {
        slug: 'hoopoe-on-the-pipe',
        title: 'Hoopoe',
        latin: 'Upupa epops',
        realm: 'field',
        note: 'His favourite subject, balanced on a rusted pipe.',
        alt: 'A hoopoe with its crest partly raised, perched on a rusted metal pipe against a soft green background.',
        image: hoopoeOnPipe,
        tone: '#4e5934',
        focus: '35% 45%',
        source: fromPx(1006264838)
    },
    {
        slug: 'hoopoe-in-the-grass',
        title: 'Hoopoe',
        latin: 'Upupa epops',
        realm: 'field',
        note: 'Crest fully fanned, low in the spring grass.',
        alt: 'A hoopoe in green grass with its orange, black-tipped crest fully fanned.',
        image: hoopoeInTheGrass,
        tone: '#4e6a2a',
        focus: '42% 50%',
        source: fromInterview
    },
    {
        slug: 'grey-partridge',
        title: 'Grey partridge',
        latin: 'Perdix perdix',
        realm: 'field',
        note: 'Low sun on a field edge. A bird he has said he now sees less often.',
        alt: 'A grey partridge standing on a grassy mound in warm low sunlight, with a soft brown background.',
        image: greyPartridge,
        tone: '#6a5135',
        focus: '45% 50%',
        source: fromPx(1007373465)
    },
    {
        slug: 'lapwing-chick',
        title: 'Lapwing chick',
        latin: 'Vanellus vanellus',
        realm: 'field',
        note: 'All legs and down, sprinting across the tarmac.',
        alt: 'A fluffy northern lapwing chick running across a grey road surface.',
        image: lapwingChick,
        tone: '#a5a5a2',
        focus: '42% 55%',
        source: fromPx(307379965)
    },
    {
        slug: 'bee-eaters-landing',
        title: 'European bee-eaters',
        latin: 'Merops apiaster',
        realm: 'field',
        note: 'A pair touching down on a mound of earth, wings still open.',
        alt: 'Two European bee-eaters landing on a mound of dry earth, wings open, against a green background.',
        image: beeEatersLanding,
        tone: '#3d5a2a',
        focus: '50% 45%',
        source: fromPx(307380313)
    },
    {
        slug: 'bee-eaters-on-the-wire',
        title: 'European bee-eaters',
        latin: 'Merops apiaster',
        realm: 'field',
        note: 'Three on a wire: one watches, one calls, one preens.',
        alt: 'Three European bee-eaters on a wire, one upright, one calling with its bill open, one preening.',
        image: beeEatersWire,
        tone: '#d6d8da',
        focus: '50% 50%',
        source: fromPx(307380421)
    },
    {
        slug: 'goldfinch',
        title: 'European goldfinch',
        latin: 'Carduelis carduelis',
        realm: 'field',
        note: 'A red face, a bare branch, and nothing else.',
        alt: 'A European goldfinch with a red face perched on a bare branch against an olive background.',
        image: goldfinch,
        tone: '#69632f',
        focus: '55% 35%',
        source: fromPx(307380033)
    },
    {
        slug: 'red-backed-shrike',
        title: 'Red-backed shrike',
        latin: 'Lanius collurio',
        realm: 'field',
        note: 'The bandit mask of a male, on a thorn.',
        alt: 'A male red-backed shrike with a grey head and black eye mask perched on thorny twigs.',
        image: redBackedShrike,
        tone: '#335121',
        focus: '40% 40%',
        source: fromPx(307379409)
    },
    {
        slug: 'black-headed-wagtail',
        title: 'Black-headed wagtail',
        latin: 'Motacilla flava feldegg',
        realm: 'field',
        note: 'Bright yellow against dry thistle stems.',
        alt: 'A black-headed yellow wagtail perched on a dry thistle stem against a soft beige background.',
        image: blackHeadedWagtail,
        tone: '#a89a80',
        focus: '40% 45%',
        source: fromPx(307380191)
    },
    {
        slug: 'hooded-crow',
        title: 'Hooded crow',
        latin: 'Corvus cornix',
        realm: 'field',
        note: 'A portrait of a bird most people walk straight past.',
        alt: 'A close-up portrait of a hooded crow’s head and grey shoulders against a bright yellow-green background.',
        image: hoodedCrow,
        tone: '#b8b841',
        focus: '40% 45%',
        source: fromPx(1007373532)
    },
    {
        slug: 'turtle-doves',
        title: 'European turtle doves',
        latin: 'Streptopelia turtur',
        realm: 'field',
        note: 'A pair on a single wire, stacked like notes on a stave.',
        alt: 'Two European turtle doves perched on a vertical wire against a blue sky with clouds.',
        image: turtleDoves,
        tone: '#8eb8df',
        focus: '55% 50%',
        source: fromPx(307379753)
    },
    {
        slug: 'green-lizard',
        title: 'Green lizard',
        latin: 'Lacerta sp.',
        realm: 'field',
        note: 'A head rising from the rocks into a single blade of light.',
        alt: 'A green lizard’s head emerging from dark rocks into a narrow beam of light.',
        image: greenLizard,
        tone: '#0c0a07',
        focus: '52% 45%',
        source: fromInterview
    },
    {
        slug: 'nose-horned-viper',
        title: 'Nose-horned viper',
        latin: 'Vipera ammodytes',
        realm: 'field',
        note: 'Europe’s most venomous snake, resting along a fallen trunk.',
        alt: 'A close-up of a nose-horned viper’s head resting on a log, its small horn visible, against a dark green background.',
        image: noseHornedViper,
        tone: '#0c2811',
        focus: '50% 50%',
        source: fromInterview
    },
    {
        slug: 'nursery-web-spider',
        title: 'Nursery web spider',
        latin: 'Pisaura mirabilis',
        realm: 'field',
        note: 'Carrying her egg sac up a single blade of grass.',
        alt: 'A nursery web spider climbing a green stem while carrying a large white egg sac.',
        image: nurseryWebSpider,
        tone: '#8b894a',
        focus: '50% 45%',
        source: fromPx(1006243585)
    },
    {
        slug: 'stork-on-the-dome',
        title: 'White stork',
        latin: 'Ciconia ciconia',
        realm: 'field',
        note: 'A nest built beside the crescent of a mosque dome, under a heavy sky.',
        alt: 'A white stork sitting on a large nest built on a metal dome beside a crescent finial, under a stormy sky.',
        image: storkOnDome,
        tone: '#4a595d',
        focus: '45% 50%',
        source: fromPx(307380349)
    },
    {
        slug: 'storks-on-the-nest',
        title: 'White storks',
        latin: 'Ciconia ciconia',
        realm: 'field',
        note: 'Look closely at the nest: plastic, woven in with the branches.',
        alt: 'Two white storks on a large nest, one with wings spread, with pieces of plastic caught in the branches.',
        image: storksOnNest,
        tone: '#223221',
        focus: '50% 40%',
        source: fromPx(307380385)
    }
]

export const photographs: Array<Photograph> = entries.map((entry, index) => ({
    ...entry,
    plate: index + 1
}))

export const getPhotograph = (slug: string): Photograph | undefined => {
    return photographs.find(photo => photo.slug === slug)
}

export const pick = (slug: string): Photograph => {
    const photo = getPhotograph(slug)

    if (!photo) {
        throw new Error(`Unknown photograph: ${slug}`)
    }

    return photo
}

export const byRealm = (realm: Realm): Array<Photograph> => {
    return photographs.filter(photo => photo.realm === realm)
}

export const getRealm = (realm: Realm): RealmInfo => {
    return realms.find(item => item.id === realm) ?? realms[0]
}

export const formatPlate = (plate: number): string => {
    return plate.toString().padStart(2, '0')
}

export const getNeighbours = (slug: string): { previous: Photograph, next: Photograph } => {
    const index = photographs.findIndex(photo => photo.slug === slug)
    const total = photographs.length

    return {
        previous: photographs[(index - 1 + total) % total],
        next: photographs[(index + 1) % total]
    }
}

const hexToRgb = (hex: string): [number, number, number] => {
    const value = hex.replace('#', '')
    const full = value.length === 3 ? value.split('').map(char => char + char).join('') : value

    return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16)
    ]
}

export const isLightTone = (hex: string): boolean => {
    const [r, g, b] = hexToRgb(hex).map(channel => {
        const c = channel / 255

        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })

    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.18
}
